import React from 'react';
import { FaCheck } from 'react-icons/fa';
import { ChakraV3 } from '..';

const paletteColors = {
  blue: '#dbeafe',
  cyan: '#cffafe',
  gray: '#f4f4f5',
  green: '#dcfce7',
  orange: '#ffedd5',
  pink: '#fce7f3',
  purple: '#f3e8ff',
  red: '#fee2e2',
  teal: '#ccfbf1',
  yellow: '#fef9c3',
} as const;

const hexPresetColors = {
  black: '#000000',
  gray: '#718096',
  blue: '#2979ff',
  cyan: '#00e5ff',
  green: '#00e676',
  orange: '#ff9100',
  purple: '#d500f9',
  red: '#ff1744',
  teal: '#1de9b6',
  yellow: '#ffea00',
} as const;

export interface ColorPickerProps {
  mode: 'hex' | 'palette';
  value: string;
  onChange: (color: string) => void;
}

export const ColorPicker = (props: ColorPickerProps) => {
  const { mode, value, onChange, ...rest } = props;

  const isHex = mode === 'hex';
  const swatches = isHex ? hexPresetColors : paletteColors;

  const parsedValue = isHex
    ? ChakraV3.parseColor(value || hexPresetColors.blue)
    : ChakraV3.parseColor(paletteColors[value as keyof typeof paletteColors] ?? paletteColors.blue);

  const onValueChange = (details: ChakraV3.ColorPicker.ValueChangeDetails) => {
    if (isHex) {
      onChange(details.value.toString('hex'));
      return;
    }

    const hex = details.value.toString('hex').toLowerCase();
    const name = Object.entries(paletteColors).find(([, v]) => v === hex)?.[0];

    if (name) {
      onChange(name);
    }
  };

  return (
    <ChakraV3.ColorPicker.Root
      value={parsedValue}
      onValueChange={onValueChange}
      positioning={{ placement: 'bottom-start' }}
      size='sm'
      {...rest}
    >
      <ChakraV3.ColorPicker.HiddenInput />
      <ChakraV3.ColorPicker.Control>
        <ChakraV3.ColorPicker.Trigger />
      </ChakraV3.ColorPicker.Control>
      <ChakraV3.ColorPicker.Positioner>
        <ChakraV3.ColorPicker.Content p={2}>
          {isHex && (
            <>
              <ChakraV3.ColorPicker.Area />
              <ChakraV3.HStack>
                <ChakraV3.ColorPicker.EyeDropper size='xs' variant='outline' />
                <ChakraV3.ColorPicker.Sliders />
              </ChakraV3.HStack>
            </>
          )}
          <ChakraV3.ColorPicker.SwatchGroup columnGap={2}>
            {Object.entries(swatches).map(([name, hex]) => (
              <ChakraV3.ColorPicker.SwatchTrigger
                key={name}
                value={hex}
                data-pw={`colorPickerItem-${name}`}
              >
                <ChakraV3.ColorPicker.Swatch value={hex} boxSize='8' rounded='lg'>
                  <ChakraV3.ColorPicker.SwatchIndicator color='gray.400'>
                    <FaCheck />
                  </ChakraV3.ColorPicker.SwatchIndicator>
                </ChakraV3.ColorPicker.Swatch>
              </ChakraV3.ColorPicker.SwatchTrigger>
            ))}
          </ChakraV3.ColorPicker.SwatchGroup>
        </ChakraV3.ColorPicker.Content>
      </ChakraV3.ColorPicker.Positioner>
    </ChakraV3.ColorPicker.Root>
  );
};
